import { Space, Button, Tooltip, Popconfirm } from 'antd';
import { LuPencil, LuTrash2, LuEye, LuCopy } from 'react-icons/lu';
import PropTypes from 'prop-types';

/**
 * TableAction Component
 *
 * Reusable component for Table operation columns (View, Edit, Delete).
 *
 * @param {Function} onEdit - Callback for edit action. If not provided, Edit button is hidden.
 * @param {Function} onDelete - Callback for delete action. If not provided, Delete button is hidden.
 * @param {Function} onView - Callback for view details action. If not provided, View button is hidden.
 * @param {Function} onCopy - Callback for copy action. If not provided, Copy button is hidden.
 * @param {Object} record - The data record passed from Table render.
 * @param {String} deleteTitle - Title for Popconfirm.
 * @param {String} deleteDesc - Description for Popconfirm.
 */
const TableAction = ({
  record,
  onEdit,
  onDelete,
  onView,
  onCopy,
  deleteTitle = 'Xác nhận xóa?',
  deleteDesc = 'Hành động này không thể hoàn tác',
  extraActions, // Slot for additional actions
}) => {
  return (
    <Space size={8}>
      {onView && (
        <Tooltip title="Xem chi tiết">
          <Button
            className="btn-action flex items-center justify-center w-8 h-8 p-0 bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white border-none shadow-sm rounded-lg transition-all duration-200 hover:scale-110"
            icon={<LuEye size={18} />}
            onClick={(e) => {
              e.stopPropagation();
              onView(record);
            }}
          />
        </Tooltip>
      )}

      {onEdit && (
        <Tooltip title="Chỉnh sửa">
          <Button
            className="btn-action flex items-center justify-center w-8 h-8 p-0 bg-amber-50 text-amber-600 hover:bg-amber-600 hover:text-white border-none shadow-sm rounded-lg transition-all duration-200 hover:scale-110"
            icon={<LuPencil size={18} />}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(record);
            }}
          />
        </Tooltip>
      )}

      {onCopy && (
        <Tooltip title="Sao chép">
          <Button
            className="btn-action flex items-center justify-center w-8 h-8 p-0 bg-emerald-50 text-emerald-600 hover:bg-emerald-600 hover:text-white border-none shadow-sm rounded-lg transition-all duration-200 hover:scale-110"
            icon={<LuCopy size={18} />}
            onClick={(e) => {
              e.stopPropagation();
              onCopy(record);
            }}
          />
        </Tooltip>
      )}

      {extraActions}

      {onDelete && (
        <Popconfirm
          title={deleteTitle}
          description={deleteDesc}
          onConfirm={() => onDelete(record)}
          onCancel={(e) => e?.stopPropagation()}
          okText="Xóa"
          cancelText="Hủy"
          okButtonProps={{ danger: true }}
          placement="topLeft"
        >
          <Tooltip title="Xóa">
            <Button
              className="btn-action flex items-center justify-center w-8 h-8 p-0 bg-rose-50 text-rose-600 hover:bg-rose-600 hover:text-white border-none shadow-sm rounded-lg transition-all duration-200 hover:scale-110"
              icon={<LuTrash2 size={18} />}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        </Popconfirm>
      )}
    </Space>
  );
};

TableAction.propTypes = {
  record: PropTypes.object.isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onView: PropTypes.func,
  onCopy: PropTypes.func,
  deleteTitle: PropTypes.string,
  deleteDesc: PropTypes.string,
  extraActions: PropTypes.node,
};

export default TableAction;
